import React, { useEffect, useRef, useState } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";

// "Continue with Google" via Google Identity Services. The backend verifies the ID token.
// Renders nothing until the backend reports a GOOGLE_CLIENT_ID, so it is safe to ship unconfigured.
const GSI_SRC = "https://accounts.google.com/gsi/client";
let gsiPromise;
function loadGsi() {
    if (window.google?.accounts?.id) return Promise.resolve();
    if (!gsiPromise) {
        gsiPromise = new Promise((resolve, reject) => {
            const s = document.createElement("script");
            s.src = GSI_SRC; s.async = true; s.defer = true;
            s.onload = resolve;
            s.onerror = () => { gsiPromise = null; reject(new Error("gsi load failed")); };
            document.head.appendChild(s);
        });
    }
    return gsiPromise;
}

export default function GoogleSignIn({ onSuccess, text = "continue_with", children }) {
    const btnRef = useRef(null);
    const [clientId, setClientId] = useState("");
    const [busy, setBusy] = useState(false);
    const cb = useRef(onSuccess);
    cb.current = onSuccess;

    useEffect(() => {
        let alive = true;
        api.get("/auth/google/config").then(({ data }) => { if (alive) setClientId(data?.client_id || ""); }).catch(() => {});
        return () => { alive = false; };
    }, []);

    useEffect(() => {
        if (!clientId) return;
        let alive = true;
        loadGsi().then(() => {
            if (!alive || !btnRef.current) return;
            window.google.accounts.id.initialize({
                client_id: clientId,
                ux_mode: "popup",
                callback: async ({ credential }) => {
                    if (!credential) return;
                    setBusy(true);
                    try { await cb.current(credential); }
                    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
                    finally { setBusy(false); }
                },
            });
            const width = Math.min(400, btnRef.current.parentElement?.offsetWidth || 400);
            window.google.accounts.id.renderButton(btnRef.current, {
                type: "standard", theme: "outline", size: "large", shape: "rectangular",
                text, logo_alignment: "center", width,
            });
        }).catch(() => {});
        return () => { alive = false; };
    }, [clientId, text]);

    if (!clientId) return null;
    return (
        <div className="mt-8 -mb-6 max-w-md" data-testid="google-signin">
            <div ref={btnRef} className={`flex justify-center ${busy ? "opacity-50 pointer-events-none" : ""}`} style={{ minHeight: 44 }} />
            {children}
        </div>
    );
}
